import { useEffect, useMemo, useState } from "react";
import { ImageOff, Maximize } from "lucide-react";

import { cn } from "@/lib/utils";
import type { TFunction } from "@/i18n/catalog";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency, formatWeight } from "@/lib/format";
import { foodLabel } from "@/lib/food-labels";
import { isPartOfDay } from "@/lib/kpi";
import { selectPulse } from "@/lib/pulse";
import { yesterdayRange, kitchenDailyTarget, formatCaptureTime } from "@/lib/kitchen-display";
import { useWasteStats } from "@/hooks/use-waste-stats";
import { useLineStats } from "@/hooks/use-line-stats";
import { useProofEvents } from "@/hooks/use-proof-events";
import { useFoodSpendPeriods, estimateRangeSpend } from "@/hooks/use-food-spend";
import type { WasteEvent } from "@/hooks/use-waste-events";
import type { WasteFilters } from "@/hooks/use-waste-filters";

/** Poll interval while the display is open — "updates after each service". */
const REFETCH_MS = 60_000;
/** Grace before deciding fullscreen didn't engage, so a fresh open (fullscreen
 * still transitioning) doesn't flash the re-enter button. */
const FULLSCREEN_RECONCILE_MS = 600;

type Props = {
  siteId: string | null;
  siteName: string;
  /** Shared dashboard scope (status = successful) — keeps the verdict aligned
   * with the homepage headline and The Proof feed. */
  filters: WasteFilters;
  currency: string;
  /** Resolved site timezone — "yesterday" is the site's day, not the viewer's. */
  timezone: string;
  /** Resolved `waste_pct_target` (POS2-866); null when unset → neutral tile. */
  targetPct: number | null;
  onExit: () => void;
};

/**
 * Kitchen Display mode (POS2-878): a full-screen dark wall verdict of yesterday's
 * service. Three glove-sized tiles — waste £ vs the derived daily target, the line
 * that worked, and the highest-value photographed capture — poll live while open.
 * Esc and the fullscreen-exit both close it (converging on `onExit`).
 */
export function KitchenDisplay({
  siteId,
  siteName,
  filters,
  currency,
  timezone,
  targetPct,
  onExit,
}: Props) {
  const { locale, t } = useTranslation();
  const range = useMemo(() => yesterdayRange(new Date(), timezone), [timezone]);

  // Same food-only universe as the homepage headline (POS2-861). Only the total
  // cost is read, so request just that dim (POS2-960).
  const statsQuery = useWasteStats(filters, range, {
    refetchInterval: REFETCH_MS,
    excludeWasteType: "packaging",
    dims: ["total"],
  });
  // Food spend for that one civil day (the `date` columns take calendar days, not instants).
  const foodSpendQuery = useFoodSpendPeriods(siteId, { from: range.day, to: range.day }, range.day);
  const lineStatsQuery = useLineStats({
    siteId,
    periodStart: range.from,
    periodLengthDays: 1,
    nPeriods: 2,
    topN: 50,
    filters,
    refetchInterval: REFETCH_MS,
  });
  // Only the single highest-value capture is shown (no per-line dedupe here), so
  // fetch one row — avoids the endpoint pre-signing 24 R2 image URLs each poll.
  const proofQuery = useProofEvents(filters, range, 1, { refetchInterval: REFETCH_MS });

  // Offer a one-tap re-enter after a reload restores the overlay from the URL
  // without fullscreen (the browser won't re-enter it without a fresh gesture).
  // Start "true" so a fresh open — where fullscreen is still transitioning in —
  // never flashes the button; reconcile once the transition has had time to land.
  const [isFullscreen, setIsFullscreen] = useState(true);
  const enterFullscreen = () => {
    void document.documentElement.requestFullscreen?.().catch(() => {});
  };

  // Esc closes; so does leaving fullscreen (the browser swallows Esc as a
  // fullscreen exit, firing `fullscreenchange` instead of a keydown we'd see).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onExit();
    };
    const onFsChange = () => {
      const active = !!document.fullscreenElement;
      setIsFullscreen(active);
      if (!active) onExit();
    };
    const reconcile = setTimeout(
      () => setIsFullscreen(!!document.fullscreenElement),
      FULLSCREEN_RECONCILE_MS,
    );
    document.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      clearTimeout(reconcile);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
    };
  }, [onExit]);

  const wasteCost = statsQuery.data?.total_cost ?? null;
  // One civil day, and the query only returns periods overlapping it, so every day here
  // is an entered day — the daily-average fill (POS2-996) never engages.
  const daySpend =
    estimateRangeSpend(foodSpendQuery.data, { from: range.day, to: range.day }, range.day)?.total ??
    null;
  // The target is built from the whole day's food spend, so part of a day's waste is not
  // measured against it (POS2-1492).
  const partOfDay = isPartOfDay(filters);
  const target = partOfDay ? null : kitchenDailyTarget(daySpend, targetPct);
  const over = target != null && wasteCost != null ? wasteCost > target : null;

  const worked = selectPulse(lineStatsQuery.data?.lines ?? [])?.working ?? null;
  const capture = proofQuery.data?.events?.[0] ?? null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-[#0c1210] px-10 py-[34px] font-display text-[#f4f7f4]"
      role="dialog"
      aria-modal="true"
      aria-label={t("dashboard.kitchenLabel")}
      data-testid="kitchen-display"
    >
      <div className="mb-[26px] flex items-center justify-between gap-4">
        <div>
          <div className="text-[22px] font-extrabold tracking-[-0.4px]">{siteName}</div>
          <div className="mt-1 font-sans text-[13px] font-semibold text-[#7f8f85]">
            {t("dashboard.kitchenTitle")}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isFullscreen && (
            <button
              type="button"
              onClick={enterFullscreen}
              className="inline-flex min-h-[52px] items-center gap-2 rounded-[10px] border border-[#33403a] bg-transparent px-[22px] py-3 font-sans text-[15px] font-bold text-[#c8d4cc] transition-colors hover:bg-[#18211d]"
              data-testid="kitchen-fullscreen"
            >
              <Maximize className="size-4" aria-hidden />
              {t("dashboard.kitchenFullscreen")}
            </button>
          )}
          <button
            type="button"
            onClick={onExit}
            className="min-h-[52px] rounded-[10px] border border-[#33403a] bg-transparent px-[22px] py-3 font-sans text-[15px] font-bold text-[#c8d4cc] transition-colors hover:bg-[#18211d]"
            data-testid="kitchen-exit"
          >
            {t("dashboard.kitchenExit")} ✕
          </button>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-1 content-stretch gap-[22px] min-[1100px]:grid-cols-3">
        {/* Waste yesterday — the money verdict */}
        <Tile>
          <Label>{t("dashboard.kitchenWasteYesterday")}</Label>
          <div
            className={cn(
              "text-[clamp(44px,5.5vw,76px)] font-black leading-none tracking-[-2px]",
              over === true
                ? "text-[#f87171]"
                : over === false
                  ? "text-[#4ade80]"
                  : "text-[#f4f7f4]",
            )}
            data-testid="kitchen-waste-value"
            data-tone={over == null ? "neutral" : over ? "bad" : "good"}
          >
            {wasteCost != null ? formatCurrency(locale, wasteCost, currency) : "—"}
          </div>
          <Secondary>
            {target != null && wasteCost != null ? (
              <>
                {t("dashboard.kitchenTarget", {
                  target: formatCurrency(locale, target, currency),
                })}{" "}
                ·{" "}
                <b className="font-bold text-white">
                  {formatCurrency(locale, Math.abs(wasteCost - target), currency)}{" "}
                  {over ? t("dashboard.kitchenOver") : t("dashboard.kitchenUnder")}
                </b>
              </>
            ) : partOfDay ? (
              <span data-testid="kitchen-part-of-day">{t("dashboard.kitchenPartOfDay")}</span>
            ) : (
              <span data-testid="kitchen-no-target">{t("dashboard.kitchenNoTarget")}</span>
            )}
          </Secondary>
        </Tile>

        {/* Worked yesterday — the line that dropped off */}
        <Tile>
          <Label>{t("dashboard.kitchenWorkedYesterday")}</Label>
          {worked ? (
            <>
              <div
                className="text-[clamp(30px,3.2vw,46px)] font-black leading-[1.15] tracking-[-1px] text-[#4ade80]"
                data-testid="kitchen-worked-label"
              >
                {foodLabel(t, worked.label)}
              </div>
              <Secondary>
                <b className="font-bold text-white">
                  {t("dashboard.kitchenCaptures", { count: worked.captures })}
                </b>{" "}
                · {t("dashboard.kitchenPrevCaptures", { count: worked.prevCaptures })}
              </Secondary>
            </>
          ) : (
            <Secondary>
              <span data-testid="kitchen-no-worked">{t("dashboard.kitchenNoWorked")}</span>
            </Secondary>
          )}
        </Tile>

        {/* Didn't work yesterday — the photographed proof */}
        <Tile className="border-[#5c2a2a] bg-[#1c1210]">
          <Label className="text-[#e8a2a2]">{t("dashboard.kitchenDidntWork")}</Label>
          <div className="relative grid min-h-[150px] flex-1 place-items-center overflow-hidden rounded-[14px] bg-[radial-gradient(ellipse_at_40%_35%,#3a4a3a_0%,#232d24_55%,#161c17_100%)]">
            {capture?.image_read_url ? (
              <img
                src={capture.image_read_url}
                alt={t("dashboard.kitchenCaptureAlt")}
                className="size-full object-cover"
                data-testid="kitchen-capture-image"
              />
            ) : (
              <ImageOff className="size-16 text-[#3a4a3a]" aria-hidden />
            )}
            {capture && (
              <span
                className="absolute bottom-[10px] left-3 rounded-full bg-[#0c1210]/65 px-[9px] py-[3px] font-sans text-[12px] text-[#c8d4cc]"
                data-testid="kitchen-capture-caption"
              >
                {[
                  capture.device_serial,
                  formatCaptureTime(locale, capture.logged_at, capture.timezone),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            )}
          </div>
          <Secondary>
            {capture ? (
              <>
                <b className="font-bold text-white">{captureLabel(t, capture)}</b> ·{" "}
                {formatWeight(locale, capture.item_weight)}
                {capture.cost_estimate != null &&
                  ` · ${formatCurrency(locale, capture.cost_estimate, currency)}`}
              </>
            ) : (
              <span data-testid="kitchen-no-capture">{t("dashboard.kitchenNoCapture")}</span>
            )}
          </Secondary>
        </Tile>
      </div>

      <div className="mt-[22px] flex flex-wrap justify-between gap-2 font-sans text-[14px] text-[#5d6d64]">
        <span>{t("dashboard.kitchenFooterLeft")}</span>
        <span>{t("dashboard.kitchenFooterRight")}</span>
      </div>
    </div>
  );
}

function Tile({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "flex flex-col justify-center gap-[14px] rounded-[18px] border border-[#22302a] bg-[#121a16] p-[30px]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function Label({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "font-sans text-[14px] font-bold uppercase tracking-[0.12em] text-[#7f8f85]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function Secondary({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-sans text-[clamp(17px,1.6vw,22px)] font-semibold leading-[1.4] text-[#c8d4cc]">
      {children}
    </div>
  );
}

/** The capture's item label — most specific known name (mirrors The Proof's card title). */
function captureLabel(t: TFunction, event: WasteEvent): string {
  return (
    foodLabel(t, event.inventory_item_name) ??
    foodLabel(t, event.item?.label) ??
    foodLabel(t, event.subcategory_name) ??
    foodLabel(t, event.category_name) ??
    t("dashboard.proofUnidentified")
  );
}
